/**
 * Updates a specific user's allocated credits by a delta value.
 *
 * @param {number} delta - The amount to add or subtract (e.g., +1 or -1).
 * @param {string|number} userId - The unique id of the user to update.
 * @param {Function} setData - React state setter for the data array.
 * @param {boolean} [canIncrement=true] - Whether incrementing is allowed (based on unassigned credits).
 */
export const updateCredits = (delta, userId, setData, canIncrement = true) => {
  setData((prev) => {
    const row = prev.find((r) => r.id === userId);
    if (!row || (delta > 0 && !canIncrement) || (delta < 0 && row.allocatedCredits <= 0))
      return prev;

    return prev.map((row) => {
      if (row.id === userId) {
        const newAllocated = (row.allocatedCredits ?? 0) + delta;
        const newDiff = newAllocated - (row.startingCredits ?? 0);
        return {
          ...row,
          allocatedCredits: newAllocated,
          diff: newDiff,
          neg_diff: newDiff < 0 ? newDiff : undefined,
          pos_diff: newDiff > 0 ? newDiff : undefined,
        };
      }
      return row;
    });
  });
};

/**
 * Handles incrementing allocated credits for a row, only if unassigned credits are available.
 *
 * @param {string|number} userId - The unique id of the user to increment.
 * @param {Function} setData - React state setter for the data array.
 * @param {number} unassignedCredits - Current count of unassigned credits.
 */
export const handleIncrement = (userId, setData, unassignedCredits) => {
  updateCredits(0.5, userId, setData, unassignedCredits > 0);
};

/**
 * Handles decrementing allocated credits for a row (always allowed unless already at 0).
 *
 * @param {string|number} userId - The unique id of the user to decrement.
 * @param {Function} setData - React state setter for the data array.
 */
export const handleDecrement = (userId, setData) => {
  updateCredits(-0.5, userId, setData, true);
};

/**
 * Generates table column configurations, including editable cells and custom renderers.
 *
 * @param {React.Component} AllocatedCreditsCell - Custom cell component for editing allocated credits.
 * @param {React.Component} NegDiffCell - Custom cell for negative difference display.
 * @param {React.Component} PosDiffCell - Custom cell for positive difference display.
 * @param {Array} USER_CREDITS_ITEM_COLUMNS - Column definitions from backend or static config.
 * @param {boolean} isEditable - Whether cells should render with editable controls.
 * @param {Object} styles - Imported module styles.
 * @param {number} totalUserCredits - Current total of all allocated credits.
 * @param {number} unassignedCredits - Remaining credits that can be allocated.
 * @param {Function} updateDataAndFiltered - Callback to update data in parent state.
 * @returns {Array} Array of column definitions for use in a table component.
 */
export const makeColumns = (
  AllocatedCreditsCell,
  NegDiffCell,
  PosDiffCell,
  USER_CREDITS_ITEM_COLUMNS,
  isEditable,
  styles,
  totalUserCredits,
  unassignedCredits,
  updateDataAndFiltered
) => {
  const columns = USER_CREDITS_ITEM_COLUMNS.map((col) => {
    const accessor = col.accessor || col.accessorKey;

    if (!isEditable && (accessor === 'pos_diff' || accessor === 'neg_diff')) {
      return null;
    }

    if (accessor === 'allocatedCredits') {
      return {
        ...col,
        // eslint-disable-next-line react/prop-types
        Cell: ({ getValue, row }) => {
          const currentValue = getValue() || 0;

          return (
            <AllocatedCreditsCell
              disableMinus={!currentValue || currentValue <= 0}
              disablePlus={unassignedCredits <= 0}
              isEditable={isEditable}
              // eslint-disable-next-line react/prop-types
              onDecrement={() => handleDecrement(row.original.id, updateDataAndFiltered)}
              onIncrement={() =>
                // eslint-disable-next-line react/prop-types
                handleIncrement(row.original.id, updateDataAndFiltered, unassignedCredits)
              }
              value={currentValue || 0}
            />
          );
        },
        className: col.className ? styles[col.className] : undefined,
        Footer: (
          <span className={styles['credit-management-allocated-credits-footer']}>
            {totalUserCredits}
          </span>
        ),
      };
    }

    if (accessor === 'neg_diff') {
      return {
        ...col,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => <NegDiffCell row={row} />,
      };
    }

    if (accessor === 'pos_diff') {
      return {
        ...col,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => <PosDiffCell row={row} />,
      };
    }

    return col;
  }).filter(Boolean);

  return columns;
};
